import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, CreditCard, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPaymentLogs = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'operator') {
      fetchLogs();
    }
  }, [user]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/payment-logs`);
      setLogs(response.data);
    } catch (error) {
      toast.error('獲取充值記錄失敗');
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => 
    log.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.stripe_payment_intent_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalAmount = filteredLogs.reduce((sum, log) => sum + (log.amount_hkd || 0), 0);
  const totalCredits = filteredLogs.reduce((sum, log) => sum + (log.credits_purchased || 0), 0);

  return (
    <AdminLayout>
      <div className="flex flex-col h-full overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-neutral-200 px-8 py-6 flex-shrink-0">
          <div>
            <h1 className="text-3xl font-heading font-bold text-neutral-900">充值訂單記錄</h1>
            <p className="text-neutral-600 mt-1">查看所有用戶通過 Stripe 充值的記錄</p>
          </div>
        </header>

        {/* Stats */}
        <div className="bg-white border-b border-neutral-200 px-8 py-4 flex-shrink-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="text-sm text-green-700 mb-1">總充值金額</div>
              <div className="text-2xl font-bold text-green-900">HK$ {totalAmount.toFixed(2)}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="text-sm text-blue-700 mb-1">總購買點數</div>
              <div className="text-2xl font-bold text-blue-900">{totalCredits.toFixed(0)} 點</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <div className="text-sm text-purple-700 mb-1">訂單數量</div>
              <div className="text-2xl font-bold text-purple-900">{filteredLogs.length}</div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white border-b border-neutral-200 px-8 py-4 flex-shrink-0">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                placeholder="搜索用戶郵箱或交易ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-lg"
              />
            </div>
            <Button onClick={fetchLogs} variant="outline" className="rounded-lg">
              刷新
            </Button>
          </div>
        </div>

        {/* Logs Table */}
        <main className="flex-1 overflow-y-auto p-8">
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">時間</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">用戶</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">充值金額 (HKD)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">購買點數</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">交易狀態</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Stripe ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-neutral-500">
                        載入中...
                      </td>
                    </tr>
                  ) : filteredLogs.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-neutral-500">
                        暫無充值記錄
                      </td>
                    </tr>
                  ) : (
                    filteredLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-neutral-50">
                        <td className="px-6 py-4 text-sm text-neutral-900">
                          {new Date(log.created_at).toLocaleString('zh-TW', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="px-6 py-4 text-sm text-neutral-900">{log.user_email}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-green-600">
                          HK$ {log.amount_hkd?.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-blue-600">
                          {log.credits_purchased?.toFixed(0)} 點
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {log.status === 'succeeded' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3" />
                              成功
                            </span>
                          ) : log.status === 'failed' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              <XCircle className="w-3 h-3" />
                              失敗
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700">
                              {log.status}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-xs text-neutral-500 font-mono">
                          {log.stripe_payment_intent_id?.substring(0, 20)}...
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </main>
      </div>
    </AdminLayout>
  );
};

export default AdminPaymentLogs;
